import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { RestaurantBranding } from "api-types";
import type { Unsubscribe } from "firebase/firestore";

import { newId } from "@dashboard/firebaseMethods/newId";
import type { ReduxState } from "@dashboard/types/reduxState";
import { DEFAULT_THEME_ID, type Theme } from "@dashboard/types/theme";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import { ThemeInUseError } from "../backendApis/themeApi";
import type { ErrorReducerAction } from "../reducers/errorReducer";
import type { ThemeReducerAction } from "../reducers/themeReducer";
import { addError } from "./addErrorAction";

type ThemeAction = ThemeReducerAction | ErrorReducerAction;

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  ThemeReducerAction
>;

export type ThemeThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  ThemeAction
>;

// Live listener: returns the unsubscribe synchronously (not a promise).
export const subscribeToThemes =
  (
    restaurantUuid: string
  ): ThunkAction<Unsubscribe, ReduxState, ThunkExtraArgument, ThemeAction> =>
  (dispatch, getState, { themeApi }) =>
    themeApi.subscribeToThemes(
      restaurantUuid,
      (themes) => {
        // A snapshot can land after the user has switched restaurants; it
        // belongs to the restaurant this listener subscribed for, not the
        // selected one.
        if (getState().restaurant.selectedRestaurant?.uuid !== restaurantUuid) {
          return;
        }
        dispatch({
          type: "FETCHED_THEMES",
          payload: { restaurantUuid, themes },
        });
      },
      addError(dispatch)
    );

/**
 * Creates a new theme with the given name and branding.
 */
export const createTheme: (
  name: string,
  branding: RestaurantBranding
) => ThunkAction<
  Promise<string | undefined>,
  ReduxState,
  ThunkExtraArgument,
  ThemeReducerAction
> = (name, branding) => {
  return (
    dispatch: ThemeThunkDispatch,
    getState: () => ReduxState,
    { themeApi }: ThunkExtraArgument
  ) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    // The themes listener keeps this in sync, so a restaurant's first theme can
    // claim the well known id without reading the collection back.
    const id =
      getState().theme.themes.length === 0 ? DEFAULT_THEME_ID : newId();

    return themeApi
      .createTheme(currentRest.uuid, id, name, branding)
      .then(() => id)
      .catch((err) => {
        addError(dispatch)(err);
        return undefined;
      });
  };
};

/**
 * Saves branding changes to an existing theme.
 * The caller is responsible for merging branding into the theme before calling.
 */
export const saveTheme: (theme: Theme) => ThunkActionReturn = (theme) => {
  return (
    dispatch: ThemeThunkDispatch,
    getState: () => ReduxState,
    { themeApi }: ThunkExtraArgument
  ) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    return themeApi
      .saveTheme(currentRest.uuid, { ...theme, updatedAtMillis: Date.now() })
      .catch(addError(dispatch));
  };
};

/**
 * Renames an existing theme.
 */
export const renameTheme: (
  themeId: string,
  name: string
) => ThunkActionReturn = (themeId, name) => {
  return (
    dispatch: ThemeThunkDispatch,
    getState: () => ReduxState,
    { themeApi }: ThunkExtraArgument
  ) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    return themeApi
      .renameTheme(currentRest.uuid, themeId, name)
      .catch(addError(dispatch));
  };
};

/**
 * Deletes a theme.
 * Validates in Redux state first (fast), then in Firestore (authoritative).
 * Throws ThemeInUseError if any menu URL references this theme.
 */
export const deleteTheme: (themeId: string) => ThunkActionReturn = (
  themeId
) => {
  return (
    dispatch: ThemeThunkDispatch,
    getState: () => ReduxState,
    { themeApi }: ThunkExtraArgument
  ) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    // Guard: UI should prevent this, but check anyway
    const menuUrlsUsingTheme = getState().menuUrlV2.menuUrlsV2.filter(
      (m) => m.themeId === themeId
    );
    if (menuUrlsUsingTheme.length > 0) {
      return Promise.reject(new ThemeInUseError(menuUrlsUsingTheme.length));
    }

    // Firestore check happens inside themeApi.deleteTheme
    return themeApi
      .deleteTheme(currentRest.uuid, themeId)
      .catch(addError(dispatch));
  };
};
